/** @module @liquidefy/code-editor/git/git @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  GitRepositoryState,
  GitRepositoryId,
  GitStatusCode,
  GitFileStatus,
  GitStash,
  GitConfig,
  GitBranch,
  GitHead,
  StashId,
  BranchName,
  CommitHash,
  EditorResult,
  EditorEvent,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asGitRepositoryId,
  asStashId,
  asBranchName,
  asCommitHash,
  now,
  emptyGitRepositoryState,
  DEFAULT_GIT_CONFIG,
  mergeGitConfig,
} from '../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 REPOSITORY LIFECYCLE — create, initialize, validate
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create a new GitRepositoryState for a given root path.
 * Returns err if rootPath is empty or contains only whitespace.
 */
export function createRepositoryState(
  config: { readonly rootPath: string; readonly defaultBranch?: string }
): EditorResult<GitRepositoryState> {
  const rootPath = config.rootPath.trim();
  if (!rootPath) {
    return err('GIT_INVALID_ROOT_PATH', 'Repository root path cannot be empty');
  }

  const id = asGitRepositoryId(generateId('gitrepo'));
  const defaultBranch = config.defaultBranch ?? 'main';

  const state: GitRepositoryState = {
    ...emptyGitRepositoryState(id, rootPath),
    head: {
      type: 'branch',
      name: asBranchName(defaultBranch),
      commitHash: asCommitHash(''),
    },
    config: {
      ...DEFAULT_GIT_CONFIG,
      defaultBranch,
    },
  };

  const events: EditorEvent[] = [{
    kind: 'created',
    domain: 'git',
    timestamp: now(),
    payload: { repositoryId: id, rootPath },
  }];

  return ok(state, 'GIT_REPOSITORY_CREATED', `Repository created at '${rootPath}'`, events);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 STATUS — working tree status summary
// ═══════════════════════════════════════════════════════════════════════════════

/** Summary of the working tree status. */
export interface WorkingTreeStatus {
  readonly staged: GitFileStatus[];
  readonly unstaged: GitFileStatus[];
  readonly untracked: GitFileStatus[];
  readonly conflicted: GitFileStatus[];
  readonly totalChanges: number;
  readonly hasChanges: boolean;
}

/**
 * Compute a structured working tree status summary from the repository state.
 * Categorizes files by their index and worktree status codes.
 */
export function getStatus(state: GitRepositoryState): WorkingTreeStatus {
  const staged: GitFileStatus[] = [];
  const unstaged: GitFileStatus[] = [];
  const untracked: GitFileStatus[] = [];
  const conflicted: GitFileStatus[] = [];

  for (const file of state.status) {
    if (file.indexStatus === 'unmerged' || file.workTreeStatus === 'unmerged') {
      conflicted.push(file);
    } else if (file.workTreeStatus === 'untracked') {
      untracked.push(file);
    } else {
      if (file.indexStatus !== 'unmodified') staged.push(file);
      if (file.workTreeStatus !== 'unmodified') unstaged.push(file);
    }
  }

  const totalChanges = staged.length + unstaged.length + untracked.length + conflicted.length;

  return {
    staged,
    unstaged,
    untracked,
    conflicted,
    totalChanges,
    hasChanges: totalChanges > 0,
  };
}

/**
 * Update the repository's file status list.
 * The caller provides a fresh list from git status output.
 */
export function updateStatus(
  state: GitRepositoryState,
  statusLines: GitFileStatus[]
): EditorResult<GitRepositoryState> {
  const events: EditorEvent[] = [{
    kind: 'changed',
    domain: 'git',
    timestamp: now(),
    payload: { repositoryId: state.id, fileCount: statusLines.length },
  }];
  return ok(
    { ...state, status: statusLines },
    'GIT_STATUS_UPDATED',
    `Status updated: ${statusLines.length} entries`,
    events
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 STASH — save, pop, list, drop entries
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Record a stash save operation in repository state.
 * Creates a new GitStash entry and prepends it to the stash list
 * (index 0 = most recent, matching git stash behavior).
 */
export function stashSave(
  state: GitRepositoryState,
  message?: string
): EditorResult<GitStash> {
  const stash: GitStash = {
    id: asStashId(generateId('stash')),
    message: message ?? `WIP on ${state.head.name ?? 'HEAD'}`,
    branch: state.head.type === 'branch' ? state.head.name : undefined,
    date: now(),
  };

  const events: EditorEvent[] = [{
    kind: 'created',
    domain: 'git',
    timestamp: now(),
    payload: { stashId: stash.id, message: stash.message },
  }];

  // NOTE: The actual stash in the repository stashes list is updated by
  // the caller after stashSave via updateStashList — this keeps stashSave
  // as a pure factory that returns the new stash entry.
  return ok(stash, 'GIT_STASH_SAVED', `Stash saved: '${stash.message}'`, events);
}

/**
 * Add a stash entry to the repository's stash list (most recent first).
 */
export function addStashEntry(
  state: GitRepositoryState,
  stash: GitStash
): EditorResult<GitRepositoryState> {
  return ok(
    { ...state, stashes: [stash, ...state.stashes] },
    'GIT_STASH_ADDED',
    `Stash '${stash.id}' added to stash list`
  );
}

/**
 * Pop the stash at the given index (0 = most recent).
 * Removes the stash entry from the list and returns updated state.
 * Returns err if the index is out of bounds.
 */
export function stashPop(
  state: GitRepositoryState,
  index: number
): EditorResult<GitRepositoryState> {
  if (index < 0 || index >= state.stashes.length) {
    return err(
      'GIT_STASH_INDEX_OUT_OF_BOUNDS',
      `Stash index ${index} out of bounds (stash has ${state.stashes.length} entries)`
    );
  }

  const stashes = state.stashes.filter((_, i) => i !== index);
  const events: EditorEvent[] = [{
    kind: 'deleted',
    domain: 'git',
    timestamp: now(),
    payload: { repositoryId: state.id, stashIndex: index },
  }];

  return ok(
    { ...state, stashes },
    'GIT_STASH_POPPED',
    `Stash at index ${index} popped`,
    events
  );
}

/**
 * Drop a stash entry by ID.
 * Returns err if the stash ID is not found.
 */
export function stashDrop(
  state: GitRepositoryState,
  id: StashId
): EditorResult<GitRepositoryState> {
  if (!state.stashes.some(s => s.id === id)) {
    return err('GIT_STASH_NOT_FOUND', `Stash '${id}' not found`);
  }
  const stashes = state.stashes.filter(s => s.id !== id);
  return ok({ ...state, stashes }, 'GIT_STASH_DROPPED', `Stash '${id}' dropped`);
}

/**
 * Return all stash entries, most recent first.
 */
export function stashList(state: GitRepositoryState): GitStash[] {
  return [...state.stashes];
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 CLEAN — report untracked/ignored files that would be removed
// ═══════════════════════════════════════════════════════════════════════════════

/** Options for the clean operation. */
export interface CleanOptions {
  readonly includeIgnored?: boolean;
  readonly directories?: boolean;
  readonly dryRun?: boolean;
}

/**
 * Compute the list of files that `git clean` would remove.
 * This is a pure state operation — returns paths from the current status
 * that are untracked (and optionally ignored). Actual deletion is IO.
 */
export function clean(
  state: GitRepositoryState,
  options: CleanOptions = {}
): EditorResult<string[]> {
  const toRemove = state.status
    .filter(f => f.workTreeStatus === 'untracked' ||
      (options.includeIgnored && f.workTreeStatus === 'ignored'))
    .map(f => f.path);

  return ok(
    toRemove,
    'GIT_CLEAN_PREVIEW',
    `Would remove ${toRemove.length} file(s)${options.dryRun ? ' (dry run)' : ''}`
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 CONFIG — read and write git configuration
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Read a specific git config value from the repository state.
 * Supports dot-notation keys matching the GitConfig interface fields.
 */
export function getConfig(
  state: GitRepositoryState,
  key: keyof GitConfig
): unknown {
  return state.config[key];
}

/**
 * Update a git config value in the repository state.
 * Returns err if the key is not a recognized GitConfig field.
 */
export function setConfig(
  state: GitRepositoryState,
  key: keyof GitConfig,
  value: GitConfig[keyof GitConfig]
): EditorResult<GitRepositoryState> {
  const updated = mergeGitConfig(state.config, { [key]: value } as Partial<GitConfig>);
  const events: EditorEvent[] = [{
    kind: 'changed',
    domain: 'git',
    timestamp: now(),
    payload: { repositoryId: state.id, configKey: key },
  }];
  return ok(
    { ...state, config: updated },
    'GIT_CONFIG_UPDATED',
    `Git config '${key}' updated`,
    events
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 HEAD & BRANCH — update HEAD, working with branches
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Update the HEAD reference. Used when the active branch or commit changes.
 */
export function updateHead(
  state: GitRepositoryState,
  head: GitHead
): EditorResult<GitRepositoryState> {
  const events: EditorEvent[] = [{
    kind: 'changed',
    domain: 'git',
    timestamp: now(),
    payload: {
      repositoryId: state.id,
      headType: head.type,
      headName: head.name ?? head.commitHash,
    },
  }];
  return ok(
    { ...state, head },
    'GIT_HEAD_UPDATED',
    `HEAD updated to ${head.type === 'branch' ? `branch '${head.name}'` : `commit '${head.commitHash}'`}`,
    events
  );
}

/**
 * Return the current branch name, or undefined if in detached HEAD state.
 */
export function getCurrentBranchName(state: GitRepositoryState): BranchName | undefined {
  return state.head.type === 'branch' ? state.head.name : undefined;
}

/**
 * Return the branch entry for the current HEAD, if any.
 */
export function getCurrentBranchInfo(state: GitRepositoryState): GitBranch | undefined {
  const name = getCurrentBranchName(state);
  if (!name) return undefined;
  return state.branches.find(b => b.name === name);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §8 SUMMARY — repository stats for observability
// ═══════════════════════════════════════════════════════════════════════════════

/** Summary of repository state for display and diagnostics. */
export interface RepositorySummary {
  readonly repositoryId: GitRepositoryId;
  readonly rootPath: string;
  readonly currentBranch: string;
  readonly isDetachedHead: boolean;
  readonly aheadCount: number;
  readonly behindCount: number;
  readonly stagedCount: number;
  readonly unstagedCount: number;
  readonly untrackedCount: number;
  readonly conflictedCount: number;
  readonly stashCount: number;
  readonly branchCount: number;
  readonly remoteCount: number;
  readonly tagCount: number;
}

/**
 * Produce a concise summary of the repository state.
 */
export function getRepositorySummary(
  state: GitRepositoryState
): RepositorySummary {
  const status = getStatus(state);
  const branchInfo = getCurrentBranchInfo(state);

  return {
    repositoryId: state.id,
    rootPath: state.rootPath,
    currentBranch: state.head.name ?? state.head.commitHash,
    isDetachedHead: state.head.type === 'detached',
    aheadCount: branchInfo?.ahead ?? 0,
    behindCount: branchInfo?.behind ?? 0,
    stagedCount: status.staged.length,
    unstagedCount: status.unstaged.length,
    untrackedCount: status.untracked.length,
    conflictedCount: status.conflicted.length,
    stashCount: state.stashes.length,
    branchCount: state.branches.length,
    remoteCount: state.remotes.length,
    tagCount: state.tags.length,
  };
}

/**
 * Determine if the repository has any uncommitted work (staged or unstaged).
 */
export function isDirty(state: GitRepositoryState): boolean {
  return state.status.some(
    f => f.indexStatus !== 'unmodified' || f.workTreeStatus !== 'unmodified'
  );
}

/**
 * Return true if the current branch is tracking an upstream and is ahead.
 */
export function isAheadOfRemote(state: GitRepositoryState): boolean {
  const info = getCurrentBranchInfo(state);
  return (info?.ahead ?? 0) > 0;
}

/**
 * Return true if the current branch is behind its upstream.
 */
export function isBehindRemote(state: GitRepositoryState): boolean {
  const info = getCurrentBranchInfo(state);
  return (info?.behind ?? 0) > 0;
}
